import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getSuperAgents, getSuperAgentDetail, approveSuperAgent,
  getSuperSectors, assignSuperAgentSector
} from "../../../redux/actions/profile";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const PAGE_SIZE = 20;

export default function AllAgents() {
  const dispatch = useDispatch();
  const agents = useSelector((s) => s.profile.superAgents || []);
  const detail = useSelector((s) => s.profile.superAgentDetail);
  const sectors = useSelector((s) => s.profile.superSectors || []);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getSuperAgents());
    dispatch(getSuperSectors());
  }, [dispatch]);

  useEffect(() => { setPage(1); }, [search, filter]);

  const filtered = agents.filter((a) => {
    const q = search.toLowerCase();
    const matchSearch = !q || (a.fullName || "").toLowerCase().includes(q) ||
      (a.email || "").toLowerCase().includes(q) ||
      (a.agentCode || "").toLowerCase().includes(q);
    const matchFilter = filter === "all" || (filter === "approved" ? a.approve : !a.approve);
    return matchSearch && matchFilter;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openDetail = async (agent) => {
    setSelected({ agent, _loading: true });
    setLoadingDetail(true);
    await dispatch(getSuperAgentDetail(agent._id));
    setLoadingDetail(false);
    setSelected({ agent, _loading: false });
  };

  const handleApprove = async (agentId, state) => {
    setApprovingId(agentId);
    const result = await dispatch(approveSuperAgent(agentId, state));
    if (result.success) {
      dispatch(getSuperAgents());
    }
    setApprovingId(null);
  };

  const counts = {
    all: agents.length,
    approved: agents.filter((a) => a.approve).length,
    pending: agents.filter((a) => !a.approve).length,
  };

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>All Agents</h2>
          <p style={S.sub}>Platform-wide agent roster</p>
        </div>
        <span style={S.badge}>{agents.length} agents</span>
      </div>

      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <i className="bi bi-search" style={S.searchIcon} />
          <input
            style={S.search}
            placeholder="Search by name, email, code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={S.tabs}>
          {["all", "approved", "pending"].map((t) => (
            <button key={t} style={{ ...S.tab, ...(filter === t ? S.tabActive : {}) }} onClick={() => setFilter(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)} ({counts[t]})
            </button>
          ))}
        </div>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Name", "Email", "Code", "Sector", "Merchants", "Commission", "Status", "Last Login", ""].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No agents found</td></tr>
            ) : paginated.map((a) => (
              <tr key={a._id} style={S.tr}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, color: BG }}>{a.fullName || "—"}</div>
                  {a.isLead && <span style={S.leadBadge}>Lead</span>}
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#555" }}>{a.email}</td>
                <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{a.agentCode}</td>
                <td style={S.td}>{a.sector ? "Sector " + a.sector : "—"}</td>
                <td style={{ ...S.td, textAlign: "center" }}>{(a.merchants || []).length}</td>
                <td style={S.td}>
                  <span style={{ fontWeight: 700, color: a.totalEarnings > 0 ? "#5a8a00" : "#ccc" }}>
                    {a.totalEarnings > 0 ? "₦" + Number(a.totalEarnings).toLocaleString() : "—"}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ ...S.statusPill, background: a.approve ? "rgba(167,204,72,0.12)" : "rgba(246,178,58,0.12)", color: a.approve ? "#5a8a00" : "#b87800" }}>
                    {a.approve ? "Approved" : "Pending"}
                  </span>
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#888" }}>
                  {a.lastLogin ? new Date(a.lastLogin).toLocaleString() : "Never"}
                </td>
                <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button style={S.viewBtn} onClick={() => openDetail(a)}>
                      <i className="bi bi-eye" /> View
                    </button>
                    {!a.approve ? (
                      <button
                        style={{ ...S.approveBtn }}
                        disabled={approvingId === a._id}
                        onClick={() => handleApprove(a._id, true)}
                      >
                        {approvingId === a._id ? "…" : <><i className="bi bi-check-lg" /> Approve</>}
                      </button>
                    ) : (
                      <button
                        style={{ ...S.declineBtn }}
                        disabled={approvingId === a._id}
                        onClick={() => handleApprove(a._id, false)}
                      >
                        {approvingId === a._id ? "…" : <><i className="bi bi-x-lg" /> Revoke</>}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={S.pagination}>
          <span style={S.pageInfo}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={S.pageControls}>
            <button style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }} onClick={() => goTo(page - 1)} disabled={page === 1}>
              <i className="bi bi-chevron-left" />
            </button>
            {pageNumbers().map((p, i) =>
              p === "..." ? (
                <span key={`e-${i}`} style={S.ellipsis}>…</span>
              ) : (
                <button key={p} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }} onClick={() => goTo(p)}>{p}</button>
              )
            )}
            <button style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }} onClick={() => goTo(page + 1)} disabled={page === totalPages}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      )}

      {selected && (
        <DetailModal
          agent={selected.agent}
          detail={detail}
          loading={loadingDetail}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          approvingId={approvingId}
          sectors={sectors}
          onAssignSector={async (agentId, sectorId) => {
            await dispatch(assignSuperAgentSector(agentId, sectorId));
            dispatch(getSuperAgents());
          }}
        />
      )}
    </div>
  );
}

function DetailModal({ agent, detail, loading, onClose, onApprove, approvingId, sectors, onAssignSector }) {
  const mapSrc = agent.address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(agent.address)}&output=embed`
    : null;
  const [sectorId, setSectorId] = useState(agent.sectorRef?._id || agent.sectorRef || "");
  const [assigningId, setAssigningId] = useState(false);

  const handleSectorSave = async () => {
    setAssigningId(true);
    await onAssignSector(agent._id, sectorId || null);
    setAssigningId(false);
  };

  const totalEarnings = detail?.totalEarnings ?? 0;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: BG }}>{agent.fullName}</h3>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{agent.email} · {agent.agentCode}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!agent.approve ? (
              <button
                style={S.approveBtn}
                disabled={approvingId === agent._id}
                onClick={() => onApprove(agent._id, true)}
              >
                {approvingId === agent._id ? "…" : <><i className="bi bi-check-lg" /> Approve</>}
              </button>
            ) : (
              <button
                style={S.declineBtn}
                disabled={approvingId === agent._id}
                onClick={() => onApprove(agent._id, false)}
              >
                {approvingId === agent._id ? "…" : <><i className="bi bi-x-lg" /> Revoke</>}
              </button>
            )}
            <button onClick={onClose} style={S.closeBtn}><i className="bi bi-x-lg" /></button>
          </div>
        </div>

        <div style={S.modalBody}>
          {loading ? (
            <div style={S.centered}><div style={S.spinner} /></div>
          ) : (
            <>
              <div style={S.commissionBanner}>
                <i className="bi bi-cash-coin" style={{ fontSize: 20, color: PRIMARY }} />
                <div>
                  <div style={{ fontSize: 11, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Commission Earned</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: totalEarnings > 0 ? "#5a8a00" : BG }}>
                    ₦{Number(totalEarnings).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div style={S.infoGrid}>
                {[
                  ["Mobile", agent.mobile],
                  ["Sector", agent.sector ? "Sector " + agent.sector : "Unassigned"],
                  ["Status", agent.approve ? "Approved" : "Pending"],
                  ["Lead", agent.isLead ? "Yes" : "No"],
                  ["Managed By", agent.managedByAdminCode || "—"],
                  ["Last Login", agent.lastLogin ? new Date(agent.lastLogin).toLocaleString() : "Never"],
                  ["Joined", agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : "—"],
                  ["Address", agent.address || "—"],
                ].map(([k, v]) => (
                  <div key={k} style={S.infoItem}>
                    <div style={S.infoKey}>{k}</div>
                    <div style={S.infoVal}>{v || "—"}</div>
                  </div>
                ))}
              </div>

              {sectors.length > 0 && (
                <div style={S.sectorBox}>
                  <div style={S.sectionLabel}><i className="bi bi-map-fill" style={{ color: PRIMARY, marginRight: 6 }} />Assign Sector</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select
                      style={S.sectorSelect}
                      value={sectorId}
                      onChange={(e) => setSectorId(e.target.value)}
                    >
                      <option value="">— Unassigned —</option>
                      {sectors.map((sec) => (
                        <option key={sec._id} value={sec._id}>{sec.name}</option>
                      ))}
                    </select>
                    <button style={S.sectorSaveBtn} onClick={handleSectorSave} disabled={assigningId}>
                      {assigningId ? "Saving…" : "Save"}
                    </button>
                  </div>
                  {sectorId && sectors.find((s) => s._id === sectorId)?.locations?.length > 0 && (
                    <div style={S.sectorLocs}>
                      {sectors.find((s) => s._id === sectorId).locations.map((loc, i) => (
                        <span key={i} style={S.sectorLocTag}>
                          <i className="bi bi-geo-alt" style={{ fontSize: 10, marginRight: 3 }} />{loc}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {mapSrc && (
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                  <div style={S.sectionLabel}><i className="bi bi-geo-alt-fill" style={{ color: PRIMARY, marginRight: 6 }} />Agent Location</div>
                  <iframe title="map" src={mapSrc} style={S.map} frameBorder="0" allowFullScreen />
                </div>
              )}

              {detail && !detail._loading && (
                <>
                  {detail.monthlyReferrals && detail.monthlyReferrals.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={S.sectionLabel}><i className="bi bi-graph-up" style={{ color: PRIMARY, marginRight: 6 }} />Monthly Referrals</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={detail.monthlyReferrals}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 11 }} />
                          <YAxis tick={{ fill: "#888", fontSize: 11 }} allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="referrals" fill={PRIMARY} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {detail.merchants && detail.merchants.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={S.sectionLabel}><i className="bi bi-shop" style={{ color: PRIMARY, marginRight: 6 }} />Merchants ({detail.merchants.length})</div>
                      <div style={S.miniTable}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead><tr>{["Store", "Email", "Plan", "Last Login"].map((h) => <th key={h} style={S.miniTh}>{h}</th>)}</tr></thead>
                          <tbody>
                            {detail.merchants.map((m, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                <td style={S.miniTd}>{m.storeInfo?.storeName || "—"}</td>
                                <td style={S.miniTd}>{m.email}</td>
                                <td style={S.miniTd}>{m.plan || "Free"}</td>
                                <td style={S.miniTd}>{m.lastLogin ? new Date(m.lastLogin).toLocaleDateString() : "Never"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {detail.feedbacks && detail.feedbacks.length > 0 && (
                    <div>
                      <div style={S.sectionLabel}><i className="bi bi-chat-left-text" style={{ color: PRIMARY, marginRight: 6 }} />Feedbacks ({detail.feedbacks.length})</div>
                      {detail.feedbacks.slice(0, 5).map((f, i) => (
                        <div key={i} style={S.feedbackCard}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={S.sectorTag}>Sector {f.sector}</span>
                            <span style={{ fontSize: 11, color: "#bbb" }}>{f.date ? new Date(f.date).toLocaleDateString() : "—"}</span>
                          </div>
                          <p style={{ margin: 0, fontSize: 13, color: "#444" }}>{f.report}</p>
                          {f.location && <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}><i className="bi bi-geo-alt" /> {f.location}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { paddingBottom: 40, maxWidth: "100%", overflowX: "hidden" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  badge: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700 },
  toolbar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 18, flexWrap: "wrap" },
  searchWrap: { position: "relative", flex: 1, minWidth: 220 },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: 14 },
  search: { width: "100%", padding: "9px 12px 9px 36px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 13, outline: "none", boxSizing: "border-box" },
  tabs: { display: "flex", gap: 6 },
  tab: { padding: "7px 14px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#888" },
  tabActive: { background: PRIMARY, borderColor: PRIMARY, color: "#fff" },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", maxWidth: "100%", minWidth: 0, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 800 },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  leadBadge: { fontSize: 10, background: "rgba(155,93,229,0.12)", color: "#9b5de5", borderRadius: 10, padding: "1px 7px", marginLeft: 6, fontWeight: 700 },
  statusPill: { borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 },
  viewBtn: { background: BG, color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 },
  approveBtn: { background: "#a7cc48", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontWeight: 700 },
  declineBtn: { background: "#e63946", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontWeight: 700 },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 8 },
  pageInfo: { fontSize: 12, color: "#888" },
  pageControls: { display: "flex", alignItems: "center", gap: 4 },
  pageBtn: { minWidth: 32, height: 32, padding: "0 8px", border: "1.5px solid #e0e0e0", borderRadius: 6, background: "#fff", fontSize: 12, fontWeight: 600, color: "#555", cursor: "pointer" },
  pageBtnActive: { background: BG, borderColor: BG, color: "#fff" },
  pageBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  ellipsis: { padding: "0 4px", color: "#bbb", fontSize: 13 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" },
  modal: { background: "#fff", width: "min(560px, 95vw)", height: "100vh", overflowY: "auto", boxShadow: "-4px 0 20px rgba(0,0,0,0.15)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px 16px", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888", padding: 4 },
  modalBody: { padding: "20px 24px" },
  commissionBanner: { display: "flex", alignItems: "center", gap: 14, background: "rgba(167,204,72,0.08)", border: "1.5px solid rgba(167,204,72,0.3)", borderRadius: 10, padding: "14px 18px", marginBottom: 20 },
  centered: { display: "flex", justifyContent: "center", padding: 40 },
  spinner: { width: 32, height: 32, borderRadius: "50%", border: "3px solid " + PRIMARY, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" },
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 },
  infoItem: { background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" },
  infoKey: { fontSize: 11, color: "#aaa", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 },
  infoVal: { fontSize: 13, color: BG, fontWeight: 600 },
  map: { width: "100%", height: 220, borderRadius: 8, border: "none" },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, display: "flex", alignItems: "center" },
  miniTable: { border: "1px solid #f0f0f0", borderRadius: 8, overflowX: "auto", WebkitOverflowScrolling: "touch" },
  miniTh: { background: "#f7f7f7", padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888" },
  miniTd: { padding: "8px 10px", color: "#555" },
  feedbackCard: { background: "#f9f9f9", borderRadius: 8, padding: "12px 14px", marginBottom: 8 },
  sectorTag: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 },
  sectorBox: { background: "#f9f9f9", borderRadius: 10, padding: "14px 16px", marginBottom: 16 },
  sectorSelect: { flex: 1, border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff" },
  sectorSaveBtn: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  sectorLocs: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },
  sectorLocTag: { fontSize: 11, background: "rgba(167,204,72,0.1)", border: "1px solid rgba(167,204,72,0.25)", color: "#4a6a00", borderRadius: 20, padding: "2px 8px", display: "flex", alignItems: "center" },
};
